import { keyframes, css } from 'styled-components';

// Universal theme for GG Trophy application
export const theme = {
  colors: {
    // Primary colors from the brand
    primary: '#FFD700',
    primaryDark: '#FF8C00',
    primaryAccent: '#FF6B35',
    
    // Background gradients
    backgroundDark: '#0a0a0a',
    backgroundMid: '#1a1a2e',
    backgroundLight: '#16213e',
    
    backgroundStars:`radial-gradient(circle at 2% 4%, rgba(255, 255, 255, 0.26) 1px, transparent 0),
                      radial-gradient(circle at 29% 36%, rgba(255, 255, 255, 0.32) 1px, transparent 0),
                      radial-gradient(circle at 50% 60%, rgba(255, 255, 255, 0.2) 1px, transparent 0),
                      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.66) 1px, transparent 0),
                      radial-gradient(circle at 70% 10%, rgba(255, 255, 255, 0.11) 1px, transparent 0),
                      radial-gradient(circle at 60% 12%, rgba(255, 255, 255, 0.29) 1px, transparent 0),
                      radial-gradient(circle at 43% 90%, rgba(255, 255, 255, 0.51) 1px, transparent 0),
                      radial-gradient(circle at 32% 83%, rgba(255, 255, 255, 0.16) 1px, transparent 0),
                      radial-gradient(circle at 49% 53%, rgba(255, 255, 255, 0.6) 1px, transparent 0),
                      radial-gradient(circle at 89% 52%, rgba(255, 255, 255, 0.14) 1px, transparent 0);`,
    // Card and surface colors
    cardBackground: 'rgba(30, 30, 30, 0.1)',
    cardBackgroundThick: 'rgba(23, 36, 64, 1)',
    cardBorder: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%);',
    cardBorderHover: 'rgba(0, 215, 255, 0.4)',
    
    // Text colors
    textPrimary: '#ffffff',
    textSecondary: 'rgba(255, 255, 255, 0.8)',
    textMuted: 'rgba(255, 255, 255, 0.6)',
    textDisabled: 'rgba(255, 255, 255, 0.4)',
    
    // Input colors
    inputBackground: 'rgba(255, 255, 255, 0.05)',
    inputBorder: 'rgba(255, 255, 255, 0.1)',
    inputFocus: '#FFD700',
    
    // Status colors
    success: '#51cf66',
    successBackground: 'rgba(81, 207, 102, 0.1)',
    successBorder: 'rgba(81, 207, 102, 0.2)',
    
    error: '#ff6b6b',
    errorBackground: 'rgba(255, 107, 107, 0.1)',
    errorBorder: 'rgba(255, 107, 107, 0.2)',
    
    warning: '#ffd43b',
    warningBackground: 'rgba(255, 212, 59, 0.1)',
    warningBorder: 'rgba(255, 212, 59, 0.2)',
    
    // Interactive colors
    hover: 'rgba(255, 215, 0, 0.1)',
    active: 'rgba(255, 215, 0, 0.2)',
  },
  
  gradients: {
    primary: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
    primaryVertical: 'linear-gradient(180deg, #FFD700 0%, #FF8C00 100%)',
    background: '#000000',
    card: 'linear-gradient(135deg, rgba(10, 10, 10, 0.95) 0%, rgba(20, 20, 20, 0.95) 100%)',
    button: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
    buttonHover: 'linear-gradient(135deg, #FF8C00 0%, #FFD700 100%)',
  },
  
  shadows: {
    card: '0 20px 40px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 215, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
    button: '0 8px 25px rgba(255, 215, 0, 0.3)',
    buttonHover: '0 12px 35px rgba(255, 215, 0, 0.4)',
    logo: '0 10px 30px rgba(255, 215, 0, 0.3)',
  },
  
  borderRadius: {
    small: '8px',
    medium: '12px',
    large: '20px',
    round: '50%',
  },
  
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSizes: {
      xs: '12px',
      sm: '14px',
      md: '16px',
      lg: '18px',
      xl: '24px',
      xxl: '32px',
      display: '48px',
    },
    fontWeights: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
  },
  
  breakpoints: {
    mobile: '480px',
    tablet: '768px',
    desktop: '1024px',
    wide: '1440px',
  },
};

// Keyframe animations
export const animations = {
  fadeIn: keyframes`
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  `,
  
  sparkle: keyframes`
    0%, 50%, 100% {
      opacity: 0.5;
      transform: scale(1);
    }
    25%, 75% {
      opacity: 1;
      transform: scale(1);
    }
  `,
  
  glow: keyframes`
    0%, 100% {
      box-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
    }
    50% {
      box-shadow: 0 0 40px rgba(255, 215, 0, 0.6);
    }
  `,
  
  float: keyframes`
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-10px);
    }
  `,
};

// Common styled components using css helper
export const Container = css`
  min-height: 100vh;
  background: ${theme.gradients.background};
  position: relative;
  overflow-x: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(circle at 20% 80%, rgba(0, 215, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, rgba(0, 140, 255, 0.1) 0%, transparent 50%),
                      radial-gradient(circle at 40% 40%, rgba(138, 43, 226, 0.1) 0%, transparent 50%);
    animation: ${animations.sparkle} 4s ease-in-out infinite;
    pointer-events: none;
  }
`;

export const Card = css`
  background: ${theme.colors.cardBackground};
  border: 1px solid ${theme.colors.cardBorder};
  border-radius: ${theme.borderRadius.large};
  box-shadow: ${theme.shadows.card};
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
`;

export const Button = css`
  background: ${theme.gradients.button};
  color: #000;
  border: none;
  border-radius: ${theme.borderRadius.medium};
  font-weight: ${theme.typography.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: ${theme.shadows.button};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.buttonHover};
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;